import React, { useEffect, useRef, useState } from 'react';

import { Button, CircularProgress } from '@mui/material';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

import { formatIsoDate, getCurrentMonthStart, normalizeDayStart, parseBackendDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/stringFormat';
import {
    buildDashboardFilters,
    buildDashboardViewModel,
    CRM_DASHBOARD_MIN_DATE,
    EMPTY_DASHBOARD_DATA,
    fetchCrmDashboard
} from './crmDashboardData';
import {
    buildCrmLeadDateFilterPayload,
    CRM_LEAD_WON_PHASE_ID,
    fetchCrmLeadsPage,
    toIdArray
} from './crmLeadsData';
import { FASE_OPTIONS, formatLeadColumnValue, getLeadStatusToneClass } from './crmShared';

const KANBAN_PAGE_SIZE = 30;
const PHASE_AVERAGE_WINDOW_DAYS = 90;
const DAY_MS = 86400000;

const PHASE_DATE_FIELDS = {
    1: 'phase_one_date',
    2: 'phase_two_date',
    3: 'phase_three_date',
    4: 'phase_four_date',
    5: 'phase_five_date'
};

const TICKET_COLUMN = { field: 'ticket' };

const hasWonDateFilter = (datePayload) => Boolean(datePayload.conquistado_inicio && datePayload.conquistado_fim);

export const getKanbanPhases = (filters) => {
    const selectedIds = toIdArray(filters.fase_id).map(Number);
    return selectedIds.length === 0
        ? FASE_OPTIONS
        : FASE_OPTIONS.filter((phase) => selectedIds.includes(phase.id));
};

const buildPhaseDatePayload = (phaseId, datePayload) => {
    if (phaseId !== CRM_LEAD_WON_PHASE_ID || hasWonDateFilter(datePayload)) return datePayload;

    return {
        ...datePayload,
        conquistado_inicio: formatIsoDate(getCurrentMonthStart()),
        conquistado_fim: formatIsoDate(new Date())
    };
};

const fetchPhasePage = ({ phaseId, filters, datePayload, page }) => fetchCrmLeadsPage({
    dateFilters: buildPhaseDatePayload(phaseId, datePayload),
    filters: { ...filters, fase_id: [phaseId] },
    page,
    pageSize: KANBAN_PAGE_SIZE
});

const toColumnState = (data) => ({
    items: Array.isArray(data.items) ? data.items : [],
    total: Number(data.total) || 0,
    totalTicket: data.totalTicket ?? 0,
    page: Number(data.page) || 1,
    isLoadingMore: false
});

const buildPhaseAverageRange = () => {
    const startDate = normalizeDayStart(new Date(Date.now() - PHASE_AVERAGE_WINDOW_DAYS * DAY_MS));

    return {
        startDate: startDate < CRM_DASHBOARD_MIN_DATE ? CRM_DASHBOARD_MIN_DATE : startDate,
        endDate: new Date()
    };
};

const fetchPhaseAverages = async () => {
    const data = await fetchCrmDashboard({ dateRange: buildPhaseAverageRange(), filters: buildDashboardFilters() });
    const viewModel = buildDashboardViewModel({ ...EMPTY_DASHBOARD_DATA, ...(data ?? {}) });

    return new Map(viewModel.funnelData
        .filter((phase) => phase.averageHours !== null && phase.averageHours > 0)
        .map((phase) => [phase.id, phase.averageHours]));
};

export const useCrmLeadsKanban = ({ enabled, filters, dateFilters, refreshKey, onError }) => {
    const [columns, setColumns] = useState({});
    const [isFetching, setIsFetching] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [phaseAverages, setPhaseAverages] = useState(new Map());
    const requestId = useRef(0);
    const hasRequestedAverages = useRef(false);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    useEffect(() => {
        if (!enabled || hasRequestedAverages.current) return;
        hasRequestedAverages.current = true;

        fetchPhaseAverages()
            .then(setPhaseAverages)
            .catch(() => setPhaseAverages(new Map()));
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return undefined;

        const currentRequest = requestId.current + 1;
        requestId.current = currentRequest;
        const datePayload = buildCrmLeadDateFilterPayload(dateFilters);
        const phases = getKanbanPhases(filters);

        setIsFetching(true);

        Promise.allSettled(phases.map((phase) => fetchPhasePage({ phaseId: phase.id, filters, datePayload, page: 1 })))
            .then((results) => {
                if (requestId.current !== currentRequest) return;

                const firstError = results.find((result) => result.status === 'rejected')?.reason;

                setColumns((prev) => phases.reduce((acc, phase, index) => {
                    const result = results[index];
                    acc[phase.id] = result.status === 'fulfilled'
                        ? toColumnState(result.value)
                        : prev[phase.id] ?? { ...toColumnState({}), hasError: true };
                    return acc;
                }, { ...prev }));
                setHasLoaded(true);
                setIsFetching(false);
                if (firstError) onErrorRef.current(firstError.message);
            });

        return undefined;
    }, [dateFilters, enabled, filters, refreshKey]);

    const loadMore = async (phaseId) => {
        const column = columns[phaseId];
        if (!column || column.isLoadingMore || column.items.length >= column.total) return;

        const currentRequest = requestId.current;
        setColumns((prev) => ({ ...prev, [phaseId]: { ...prev[phaseId], isLoadingMore: true } }));

        try {
            const data = await fetchPhasePage({
                phaseId,
                filters,
                datePayload: buildCrmLeadDateFilterPayload(dateFilters),
                page: column.page + 1
            });

            if (requestId.current !== currentRequest) return;
            const nextPage = toColumnState(data);

            setColumns((prev) => ({
                ...prev,
                [phaseId]: {
                    ...nextPage,
                    items: [...prev[phaseId].items, ...nextPage.items]
                }
            }));
        } catch (error) {
            if (requestId.current !== currentRequest) return;
            setColumns((prev) => ({ ...prev, [phaseId]: { ...prev[phaseId], isLoadingMore: false } }));
            onErrorRef.current(error.message);
        }
    };

    const phases = getKanbanPhases(filters);
    const total = phases.reduce((sum, phase) => sum + (columns[phase.id]?.total ?? 0), 0);

    return { columns, hasLoaded, isFetching, loadMore, phaseAverages, phases, total };
};

const formatDayCount = (days) => `${days} ${days === 1 ? 'dia' : 'dias'}`;

const getAverageDays = (averageHours) => Math.max(1, Math.round(averageHours / 24));

const formatAverageDays = (averageHours) => formatDayCount(getAverageDays(averageHours));

const getPhaseStartDate = (row, phaseId) => parseBackendDate(row[PHASE_DATE_FIELDS[phaseId]]) ?? parseBackendDate(row.cadastro);

const buildTimeInPhase = (row, phaseId, averageHours) => {
    const startDate = getPhaseStartDate(row, phaseId);
    if (!startDate || Number.isNaN(startDate.getTime())) return { text: '', toneClass: '' };

    const elapsedMs = Math.max(Date.now() - startDate.getTime(), 0);
    const days = Math.floor(elapsedMs / DAY_MS);
    const isWon = phaseId === CRM_LEAD_WON_PHASE_ID;
    const hasAverage = !isWon && averageHours !== undefined;

    if (!hasAverage) {
        return { text: days === 0 ? 'hoje' : `há ${formatDayCount(days)}`, toneClass: '' };
    }

    const averageDays = getAverageDays(averageHours);
    const toneClass = days > averageDays * 2
        ? 'is-late'
        : days > averageDays ? 'is-warning' : '';

    return {
        text: `${days === 0 ? 'hoje' : formatDayCount(days)} · média ${averageDays}`,
        toneClass
    };
};

const getSellerInitials = (name) => {
    const words = String(name ?? '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    const first = words[0][0];
    const last = words.length > 1 ? words[words.length - 1][0] : '';
    return `${first}${last}`.toUpperCase();
};

const KanbanCard = ({ row, phaseId, averageHours, showSeller, onClick, onAuxClick, onKeyDown }) => {
    const time = buildTimeInPhase(row, phaseId, averageHours);
    const ticket = formatLeadColumnValue(TICKET_COLUMN, row.ticket);
    const initials = showSeller ? getSellerInitials(row.vendedor) : '';

    return (
        <div
            role='button'
            tabIndex={0}
            className={`crm-leads-kanban-card ${time.toneClass}`}
            aria-label={`Abrir lead ${row.lead_id}`}
            onClick={(event) => onClick(event, row)}
            onAuxClick={(event) => onAuxClick(event, row)}
            onMouseDown={(event) => event.button === 1 && event.preventDefault()}
            onKeyDown={(event) => onKeyDown(event, row)}
        >
            <div className='crm-leads-kanban-card-head'>
                <strong className='crm-leads-kanban-card-name'>{row.nome || `Lead ${row.lead_id}`}</strong>
                {initials && (
                    <span className='crm-leads-kanban-seller' title={row.vendedor}>{initials}</span>
                )}
            </div>

            {ticket && <span className='crm-leads-kanban-card-ticket'>{ticket}</span>}

            <div className='crm-leads-kanban-card-tags'>
                {row.status && (
                    <span className={`crm-leads-tone-pill crm-leads-status-pill ${getLeadStatusToneClass(row.status)}`}>
                        {row.status}
                    </span>
                )}
                {row.origem && <span className='crm-leads-kanban-card-origin'>{row.origem}</span>}
            </div>

            {time.text && (
                <span className='crm-leads-kanban-card-time'>
                    <ScheduleRoundedIcon fontSize='inherit' />
                    {time.text}
                </span>
            )}
        </div>
    );
};

const KanbanColumn = ({ phase, column, averageHours, isWonCurrentMonth, showSeller, onLoadMore, cardHandlers }) => {
    const items = column?.items ?? [];
    const hasMore = column ? items.length < column.total : false;
    const isWon = phase.id === CRM_LEAD_WON_PHASE_ID;

    return (
        <section className='crm-leads-kanban-column' aria-label={phase.label}>
            <header className='crm-leads-kanban-column-head'>
                <h3 className='crm-leads-kanban-column-title'>
                    {phase.label}
                    {isWonCurrentMonth && <span className='crm-leads-kanban-column-scope'> · este mês</span>}
                </h3>
                <p className='crm-leads-kanban-column-summary'>
                    {column ? `${column.total} · ${formatCurrency(column.totalTicket)}` : '—'}
                </p>
                {!isWon && averageHours !== undefined && (
                    <p className='crm-leads-kanban-column-average'>média: {formatAverageDays(averageHours)}</p>
                )}
            </header>

            <div className='crm-leads-kanban-column-body'>
                {items.map((row, index) => (
                    <KanbanCard
                        key={`${row.lead_id}-${index}`}
                        row={row}
                        phaseId={phase.id}
                        averageHours={averageHours}
                        showSeller={showSeller}
                        {...cardHandlers}
                    />
                ))}

                {column && items.length === 0 && (
                    <p className='crm-leads-kanban-empty'>
                        {column.hasError ? 'Não foi possível carregar esta fase' : 'Nenhum lead nesta fase'}
                    </p>
                )}

                {!column && (
                    <div className='crm-leads-kanban-loading'>
                        <CircularProgress size={20} />
                    </div>
                )}

                {hasMore && (
                    <Button
                        className='crm-leads-kanban-load-more'
                        onClick={() => onLoadMore(phase.id)}
                        disabled={column.isLoadingMore}
                    >
                        {column.isLoadingMore ? 'Carregando...' : `Carregar mais (${column.total - items.length})`}
                    </Button>
                )}
            </div>
        </section>
    );
};

export const CrmLeadsKanbanBoard = ({ kanban, filters, dateFilters, cardHandlers }) => {
    const datePayload = buildCrmLeadDateFilterPayload(dateFilters);
    const isWonCurrentMonth = !hasWonDateFilter(datePayload);
    const showSeller = toIdArray(filters.vendedor_id).length !== 1;

    return (
        <div
            className={kanban.isFetching && kanban.hasLoaded ? 'crm-leads-kanban is-fetching' : 'crm-leads-kanban'}
            aria-busy={kanban.isFetching}
        >
            {kanban.phases.map((phase) => (
                <KanbanColumn
                    key={phase.id}
                    phase={phase}
                    column={kanban.hasLoaded ? kanban.columns[phase.id] : undefined}
                    averageHours={kanban.phaseAverages.get(phase.id)}
                    isWonCurrentMonth={phase.id === CRM_LEAD_WON_PHASE_ID && isWonCurrentMonth}
                    showSeller={showSeller}
                    onLoadMore={kanban.loadMore}
                    cardHandlers={cardHandlers}
                />
            ))}
        </div>
    );
};
